import React, { useCallback, useEffect, useState } from 'react';
import { DateTime } from "luxon";
import './DashboardLayout.css';
import Header from '../Header/Hearder';
import axiosInstance from "../../lib/axiosInstance";

const DashboardLayout = ({ onNavigate, branches, selectedBranchId, onBranchChange }) => {
  const [totalAppointments, setTotalAppointments] = useState(0);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [todayRevenue, setTodayRevenue] = useState(0);
  const [loadingStats, setLoadingStats] = useState(true);
  const tz = process.env.REACT_APP_CLINIC_TZ || "Asia/Kolkata";

  useEffect(() => {
    async function loadStats() {
      setLoadingStats(true);
      try {
        const today = DateTime.now().setZone(tz);
        const date = today.toISODate(); // YYYY-MM-DD
        const dateFrom = DateTime.fromISO(date, { zone: tz }).startOf("day").toUTC().toISO();
        const dateTo = DateTime.fromISO(date, { zone: tz }).endOf("day").toUTC().toISO();

        // total appointments for today
        if (!selectedBranchId) {
          setTotalAppointments(0);
          setPendingApprovals(0);
          setTodayRevenue(0);
          return;
        }
        const apptRes = await axiosInstance.get("/appointments", { params: { branchId: selectedBranchId, dateFrom, dateTo } });
        const apptData = apptRes && apptRes.data ? apptRes.data : apptRes;
        setTotalAppointments(apptData && typeof apptData.total === "number" ? apptData.total : (Array.isArray(apptData) ? apptData.length : 0));

        // pending approvals (reserved)
        const pendRes = await axiosInstance.get("/appointments", { params: { branchId: selectedBranchId, dateFrom, dateTo, status: "scheduled" } });
        const pendData = pendRes && pendRes.data ? pendRes.data : pendRes;
        setPendingApprovals(pendData && typeof pendData.total === "number" ? pendData.total : (Array.isArray(pendData) ? pendData.length : 0));

        // revenue for today (branch-scoped)
        const revRes = await axiosInstance.get("/reports/daily-revenue", {
          params: { date, branchId: selectedBranchId },
        });
        const revData = revRes && revRes.data ? revRes.data : revRes;
        const revAmount = Number(revData?.totalRevenue || 0);
        setTodayRevenue(revAmount);
      } catch (err) {
        console.error("load dashboard stats failed", err);
      } finally {
        setLoadingStats(false);
      }
    }
    loadStats();
  }, [selectedBranchId, tz]);

  const [todaysAppointments, setTodaysAppointments] = useState([]);
  const [doctorsList, setDoctorsList] = useState([]);
  const [loadingApps, setLoadingApps] = useState(true);
  const [loadingDocs, setLoadingDocs] = useState(true);

  useEffect(() => {
    let mounted = true;
    async function loadLists() {
      setLoadingApps(true);
      setLoadingDocs(true);
      try {
        // Fetch today's appointments using explicit clinic-local day range.
        if (!selectedBranchId) {
          setTodaysAppointments([]);
          setDoctorsList([]);
          return;
        }
        const today = DateTime.now().setZone(tz).toISODate();
        const dateFrom = DateTime.fromISO(today, { zone: tz }).startOf("day").toUTC().toISO();
        const dateTo = DateTime.fromISO(today, { zone: tz }).endOf("day").toUTC().toISO();
        const appsRes = await axiosInstance.get("/appointments", {
          params: { branchId: selectedBranchId, page: 1, limit: 200, dateFrom, dateTo },
        });
        const appsData = appsRes && appsRes.data ? appsRes.data : appsRes;
        let apps = Array.isArray(appsData.items) ? appsData.items : (Array.isArray(appsData) ? appsData : []);
        // sort descending by time and keep table compact
        apps = apps
          .filter(a => a && (a.appointmentDate || a.date))
          .sort((a, b) => new Date(b.appointmentDate || b.date) - new Date(a.appointmentDate || a.date))
          .slice(0, 8);
        if (mounted) setTodaysAppointments(apps);

        const mapRes = await axiosInstance.get("/doctor-branches", { params: { branchId: selectedBranchId, isActive: true } });
        const mapData = mapRes && mapRes.data ? mapRes.data : mapRes;
        const maps = Array.isArray(mapData.items) ? mapData.items : (Array.isArray(mapData) ? mapData : []);
        const rawDocs = maps.map((m) => m.doctorId).filter(Boolean);
        const seen = new Set();
        const docs = [];
        for (const d of rawDocs) {
          const id = d && (d._id || d.id);
          const key = id != null ? String(id) : "";
          if (!key || seen.has(key)) continue;
          seen.add(key);
          docs.push(d);
        }
        if (mounted) setDoctorsList(docs);
      } catch (err) {
        console.error("load dashboard lists failed", err);
      } finally {
        if (mounted) {
          setLoadingApps(false);
          setLoadingDocs(false);
        }
      }
    }
    loadLists();
    return () => { mounted = false; };
  }, [selectedBranchId, tz]);

  const refreshTodaysAppointments = useCallback(async () => {
    try {
      if (!selectedBranchId) {
        setTodaysAppointments([]);
        return;
      }
      const today = DateTime.now().setZone(tz).toISODate();
      const dateFrom = DateTime.fromISO(today, { zone: tz }).startOf("day").toUTC().toISO();
      const dateTo = DateTime.fromISO(today, { zone: tz }).endOf("day").toUTC().toISO();
      const appsRes = await axiosInstance.get("/appointments", {
        params: { branchId: selectedBranchId, page: 1, limit: 200, dateFrom, dateTo },
      });
      const appsData = appsRes && appsRes.data ? appsRes.data : appsRes;
      let apps = Array.isArray(appsData.items) ? appsData.items : (Array.isArray(appsData) ? appsData : []);
      apps = apps
        .filter(a => a && (a.appointmentDate || a.date))
        .sort((a, b) => new Date(b.appointmentDate || b.date) - new Date(a.appointmentDate || a.date))
        .slice(0, 8);
      setTodaysAppointments(apps);
    } catch (err) {
      console.error("refresh today appointments failed", err);
    }
  }, [selectedBranchId, tz]);

  useEffect(() => {
    if (!selectedBranchId) return undefined;
    const id = setInterval(() => {
      refreshTodaysAppointments();
    }, 30000);
    return () => clearInterval(id);
  }, [selectedBranchId, refreshTodaysAppointments]);
  const cards = [
    {
      title: 'Total Appointments',
      value: loadingStats ? '—' : String(totalAppointments),
      subtitle: 'Today',
      icon: '📅',
      bgColor: '#dbeafe',
      iconColor: '#3b82f6'
    },
    {
      title: 'Pending Approvals',
      value: loadingStats ? '—' : String(pendingApprovals),
      subtitle: 'Requires attention',
      icon: '⏳',
      bgColor: '#fef3c7',
      iconColor: '#f59e0b'
    },
    {
      title: 'Total Revenue',
      value: loadingStats ? '—' : `₹${todayRevenue.toLocaleString()}`,
      subtitle: 'Today',
      icon: '₹',
      bgColor: '#d1fae5',
      iconColor: '#10b981'
    }
  ];

  return (
    <div className="dashboard">


      <Header branches={branches} selectedBranchId={selectedBranchId} onBranchChange={onBranchChange} />

      <div className="fab-container">
        <div className='context'>
          <h2>Dashboard Overview</h2>
          <span>Welcome back</span>
        </div>
        <div className='buttons'>
          <button className="fab fab-generate-bill" onClick={() => onNavigate('Billing')} >
            <span>📄</span>
            <span className="fab-text">Generate Bill</span>
          </button>

          <button className="fab fab-new-appointment" onClick={() => onNavigate('Appointments')} >
            <span>+</span>
            <span className="fab-text">New Appointment</span>
          </button>

          <button className="fab fab-generate-bill" onClick={() => onNavigate('PatientRecords')} >
            <span>+</span>
            <span className="fab-text">New Patients</span>
          </button>
        </div>
      </div>


      <div className="wrapper">
        <div className="cards-grid">
          {cards.map((card, index) => (
            <div key={index} className="stat-card">
              {/* TOP: Icon + Trend */}
              <div className="card-top">
                <div
                  className="card-icon"
                  style={{
                    backgroundColor: card.bgColor,
                    color: card.iconColor
                  }}
                >
                  <span>{card.icon}</span>
                </div>
                {card.trend && (
                  <span className="trend positive">{card.trend}</span>
                )}
              </div>

              {/* MIDDLE: Title */}
              <h3 className="card-title">{card.title}</h3>
              <span className="card-value">{card.value}</span>

              {/* BOTTOM: Value + Subtitle */}
              <div className="card-bottom">
                <div className="card-value-container">

                  <span className="card-subtitle">{card.subtitle}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Schedule Section */}
        {/* Two Column Layout - Schedule and Doctor/Queue */}
        <div className="schedule-queue-wrapper">
          {/* LEFT: Schedule Section */}
          <div className="left-column">
            <div className="schedule-section">
              <div className="schedule-header">
                <h3>Today's Schedule</h3>
                <button className="view-calendar-btn" type="button" onClick={() => onNavigate('Schedule')}>View Calendar</button>
              </div>
              <div className='hr-line'></div>

              <div className="schedule-cards-container">
                {loadingApps ? (
                  <div className="loading-placeholder">Loading appointments…</div>
                ) : todaysAppointments && todaysAppointments.length ? (
                  <div className="appointments-table-wrapper">
                    <table className="appointments-table1">
                    <thead>
                      <tr>
                        <th className="patient-col">Patient</th>
                        <th className="doctor-col">Doctor</th>
                        <th className="time-col">Slot Time</th>
                        <th className="status-col">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {todaysAppointments.slice(0, 5).map((appt) => {
                        const apptDate = appt.appointmentDate || appt.date || appt.appointment_date;
                        const dt = apptDate ? DateTime.fromISO(apptDate).setZone(tz) : null;
                        const timeLabel = dt ? dt.toLocaleString(DateTime.TIME_SIMPLE) : (appt.time || "—");
                        const patientName = appt.patientId?.name || appt.patient?.name || appt.patientName || "Unknown";
                        const doctorName = appt.doctorId?.name || appt.doctor?.name || "Doctor";
                        const statusLabel = appt.status ? (appt.status.charAt(0).toUpperCase() + appt.status.slice(1)) : "Scheduled";

                        return (
                          <tr key={appt._id || appt.id || `${patientName}-${timeLabel}`}>
                            <td className="patient-col">{patientName}</td>
                            <td className="doctor-col">{doctorName}</td>
                            <td className="time-col">{timeLabel}</td>
                            <td className="status-col">
                              <span className={`status-badge status-${(appt.status || 'scheduled').toLowerCase()}`}>
                                {statusLabel}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-placeholder">No appointments for today.</div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: Doctor and Queue Section */}
          <div className="right-column">
            <div className="doctors-header">
              <h4>Available Doctors</h4>
            </div>
            {/* Doctor Card */}
            <div className="doctors-list">
              {loadingDocs ? (
                <div className="loading-placeholder">Loading doctors…</div>
              ) : doctorsList && doctorsList.length ? (
                doctorsList.map((doc) => {
                  const name = doc.name || `${doc.firstName || ''} ${doc.lastName || ''}`.trim() || 'Doctor';
                  const initials = name.split(' ').map(n => n[0]).slice(0,2).join('').toUpperCase();
                  const active = !!doc.isActive;
                  const statusText = active ? (doc.isOnline ? 'Available • In Clinic' : 'Available') : 'Inactive';
                    return (
                      <div key={doc._id || doc.id || name} className="doctor-card small">
                        <div className="top-row">
                          <div className="top-left">
                            <div className="doctor-avatar">{initials}</div>
                            <div className="name-block">
                              <h4 className="doctor-name">{name}</h4>
                            </div>
                          </div>
                          <div className="top-right">
                            <div className="doctor-status">{statusText}</div>
                            <div className={`toggle-switch ${active ? 'active' : ''}`} />
                          </div>
                        </div>

                        <div className="info-column">
                          <div className="info-row"><span className="info-label">Spec:</span> <span className="info-val">{Array.isArray(doc.specialty) ? doc.specialty.join(', ') : (doc.specialty || '—')}</span></div>
                          <div className="info-row"><span className="info-label">Ph.no:</span> <span className="info-val">{doc.phone || '—'}</span></div>
                          {/* <div className="info-row"><span className="info-label">Mail:</span> <span className="info-val">{doc.email || '—'}</span></div> */}
                        </div>
                      </div>
                    );
                })
              ) : (
                <div className="empty-placeholder">No doctors found.</div>
              )}
            </div>

           
          </div>
        </div>

      </div>




    </div>
  );
};

export default DashboardLayout;
